import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, ArrowRight, Scales, WarningCircle, CheckCircle, Ticket } from "@phosphor-icons/react";

const TRACK = {
  legacy: { label: "Legacy NERC Process", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  mspp: { label: "MSPP Modernized", cls: "bg-indigo-100 text-indigo-900 border-indigo-300" },
};

export const TrackBadge = ({ track }) => {
  const t = TRACK[track] || TRACK.legacy;
  return <span data-testid={`track-badge-${track}`} className={`text-[11px] font-semibold px-2 py-0.5 border rounded-none ${t.cls}`}>{t.label}</span>;
};

export default function Sdp() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", standard_ref: "", process_track: "legacy", comment_deadline: "", compliance_lead_id: "", executive_approver_id: "", ballot_pool_status: "not_joined" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([api.get("/sdp/projects"), api.get("/users")]);
      setProjects(p.data.projects || []);
      setUsers(u.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Project name is required."); return; }
    try {
      const { data } = await api.post("/sdp/projects", form);
      toast.success("Project created.");
      setOpen(false);
      nav(`/sdp/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed."); }
  };

  return (
    <div data-testid="sdp-dashboard">
      <PageHeader title="Standards Development Process" subtitle="Track NERC standard-development pipelines, coordinate SME comments, and route for executive sign-off."
        action={<Button data-testid="sdp-new-project-btn" onClick={() => setOpen(true)} className="rounded-none"><Plus size={16} className="mr-1" />New Project</Button>} />

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
        projects.length === 0 ? <Empty title="No standard projects yet" hint="Create a project to begin tracking a comment or ballot cycle." /> :
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => {
            const done = p.child_ticket_done, total = p.child_ticket_total;
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <div key={p.id} data-testid={`sdp-project-card-${p.id}`} onClick={() => nav(`/sdp/${p.id}`)}
                className="border border-border bg-card p-4 cursor-pointer hover:shadow-md transition-shadow group">
                <div className="flex items-start justify-between gap-2">
                  <TrackBadge track={p.process_track} />
                  <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="font-semibold mt-2 text-base leading-tight">{p.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{p.standard_ref || "—"} · Phase: {p.current_phase || "—"}</p>

                {p.eligibility?.warning ?
                  <div data-testid={`sdp-elig-warn-${p.id}`} className="flex items-center gap-1.5 mt-3 text-[11px] text-red-700 bg-red-50 border border-red-200 px-2 py-1">
                    <WarningCircle size={13} weight="fill" /> {p.eligibility.warning}
                  </div> :
                  <div className="flex items-center gap-1.5 mt-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
                    <CheckCircle size={13} weight="fill" /> Voting eligible
                  </div>}

                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1"><Ticket size={12} /> SME tickets {done}/{total}</span>
                    <span>{p.industry_comment_count} industry comments</span>
                  </div>
                  <div className="h-1.5 bg-secondary w-full"><div className="h-1.5 bg-[#071B3E]" style={{ width: `${pct}%` }} /></div>
                  <div className="flex gap-1 flex-wrap text-[10px] text-muted-foreground pt-1">
                    {["assigned", "drafting", "in_review", "approved", "submitted"].map((s) => (
                      <span key={s} className="border border-border px-1 py-0.5">{s.replace("_", " ")}: {p.draft_stage_counts?.[s] || 0}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="sdp-create-dialog">
          <DialogHeader><DialogTitle>New Standard Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Project name</Label><Input data-testid="sdp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Project 2025-01 Inverter-Based Resources" className="rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Standard reference</Label><Input data-testid="sdp-ref" value={form.standard_ref} onChange={(e) => setForm({ ...form, standard_ref: e.target.value })} placeholder="PRC-029-1" className="rounded-none" /></div>
              <div><Label>Process track</Label>
                <Select value={form.process_track} onValueChange={(v) => setForm({ ...form, process_track: v })}>
                  <SelectTrigger className="rounded-none" data-testid="sdp-track"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="legacy">Legacy NERC Process</SelectItem><SelectItem value="mspp">MSPP Modernized</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Comment / ballot deadline</Label><Input data-testid="sdp-deadline" type="date" value={form.comment_deadline || ""} onChange={(e) => setForm({ ...form, comment_deadline: e.target.value })} className="rounded-none" /></div>
              {form.process_track === "legacy" &&
                <div><Label>Ballot pool status</Label>
                  <Select value={form.ballot_pool_status} onValueChange={(v) => setForm({ ...form, ballot_pool_status: v })}>
                    <SelectTrigger className="rounded-none" data-testid="sdp-ballot"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="not_joined">Not joined</SelectItem><SelectItem value="joined">Joined pool</SelectItem></SelectContent>
                  </Select>
                </div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Compliance Lead</Label>
                <Select value={form.compliance_lead_id} onValueChange={(v) => setForm({ ...form, compliance_lead_id: v })}>
                  <SelectTrigger className="rounded-none" data-testid="sdp-lead"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Executive approver</Label>
                <Select value={form.executive_approver_id} onValueChange={(v) => setForm({ ...form, executive_approver_id: v })}>
                  <SelectTrigger className="rounded-none" data-testid="sdp-exec"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="sdp-create-submit" onClick={create} className="w-full rounded-none">Create project</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
